import { getStatus } from '@ms/yammer-data-support';
import type { EntityStatus } from '@ms/yammer-data-support';
import type { NetworkQuestionsEnablementMode, UserAdminRoles } from '@ms/yammer-graphql';

import { editNetworkQuestionsSettingsRequestId } from '../../state/requestStatuses/types';
import type { FeatureAccessSettings, YammerState } from '../../state/types';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';

type GetFeatureAccessSettings = (state: YammerState) => FeatureAccessSettings | undefined;
/**
 * @deprecated PERFORMANCE This returns the ENTIRE FeatureAccessSettings object which will force re-renders if any
 * settings change due to any related API call. Use more specific selectors instead.
 */
export const getFeatureAccessSettings: GetFeatureAccessSettings = (state: YammerState) =>
  state.featureAccessSettings.fulfilled;

type GetFeatureAccessSettingsStatus = (state: YammerState) => EntityStatus;
export const getFeatureAccessSettingsStatus: GetFeatureAccessSettingsStatus = (state) =>
  getStatus(state.featureAccessSettings);

type HasPostOnBehalfOfStorylineFeatureAccess = (state: YammerState) => boolean;
export const hasPostOnBehalfOfStorylineFeatureAccess: HasPostOnBehalfOfStorylineFeatureAccess = (state: YammerState) =>
  !!getFeatureAccessSettings(state)?.canUseDelegateTeamsOnStoryline;

type GetCanRemoveAnswersTopics = (state: YammerState) => boolean;
export const getCanRemoveAnswersTopics: GetCanRemoveAnswersTopics = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanRemoveTopic;

type GetNetworkQuestionsEnablementMode = (state: YammerState) => NetworkQuestionsEnablementMode | undefined;
export const getNetworkQuestionsEnablementMode: GetNetworkQuestionsEnablementMode = (state) =>
  state.featureAccessSettings.fulfilled?.networkQuestionsEnablementMode;

type GetViewerCanAccessNetworkQuestions = (state: YammerState) => boolean;
export const getViewerCanAccessNetworkQuestions: GetViewerCanAccessNetworkQuestions = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanAccessNetworkQuestions;

type GetViewerCanAccessNetworkLevelInsights = (state: YammerState) => boolean;
export const getViewerCanAccessNetworkLevelInsights: GetViewerCanAccessNetworkLevelInsights = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanAccessNetworkLevelInsights;

type GetIsGroupQuestionsAIImporterEnabled = (state: YammerState) => boolean | undefined;
export const getIsGroupQuestionsAIImporterEnabled: GetIsGroupQuestionsAIImporterEnabled = (state) =>
  state.featureAccessSettings.fulfilled?.isGroupQuestionsAIImporterEnabled;

type GetIsNetworkQuestionsAIFeaturesEnabled = (state: YammerState) => boolean | undefined;
export const getIsNetworkQuestionsAIFeaturesEnabled: GetIsNetworkQuestionsAIFeaturesEnabled = (state) =>
  state.featureAccessSettings.fulfilled?.isNetworkQuestionsAIFeaturesEnabled;

type GetIsNetworkQuestionsAIImporterEnabled = (state: YammerState) => boolean | undefined;
export const getIsNetworkQuestionsAIImporterEnabled: GetIsNetworkQuestionsAIImporterEnabled = (state) =>
  state.featureAccessSettings.fulfilled?.isNetworkQuestionsAIImporterEnabled;

type GetEditNetworkQuestionsSettingsStatus = (state: YammerState) => EntityStatus;
export const getEditNetworkQuestionsSettingsStatus: GetEditNetworkQuestionsSettingsStatus = (state) =>
  getRequestStatus(state, 'EditNetworkQuestionSettings', editNetworkQuestionsSettingsRequestId);

type GetCanAssignUserAdminRoles = (state: YammerState) => boolean;
export const getCanAssignUserAdminRoles: GetCanAssignUserAdminRoles = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanAssignUserAdminRoles;

type GetAllowedUserAdminRoleAssignments = (state: YammerState) => UserAdminRoles[] | undefined;
export const getAllowedUserAdminRoleAssignments: GetAllowedUserAdminRoleAssignments = (state) =>
  state.featureAccessSettings.fulfilled?.viewerAllowedUserAdminRoleAssignments;

type GetCanDesignateLeaders = (state: YammerState) => boolean;
export const getCanDesignateLeaders: GetCanDesignateLeaders = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanDesignateLeaders;

type GetCanAccessThreadExtendedActivity = (state: YammerState) => boolean;
export const getCanAccessThreadExtendedActivity: GetCanAccessThreadExtendedActivity = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanAccessThreadExtendedActivity;

type GetCanShowMobileCrossSell = (state: YammerState) => boolean;
export const getCanShowMobileCrossSell: GetCanShowMobileCrossSell = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerHasMobileClient;

type GetViewerCanHideThreadsForNetworkInDiscovery = (state: YammerState) => boolean;
export const getViewerCanHideThreadsForNetworkInDiscovery: GetViewerCanHideThreadsForNetworkInDiscovery = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanHideThreadsForNetworkInDiscovery;

type GetIsPrivateContentModeActiveForViewer = (state: YammerState) => boolean;
export const getIsPrivateContentModeActiveForViewer: GetIsPrivateContentModeActiveForViewer = (state) =>
  !!state.featureAccessSettings.fulfilled?.isPrivateContentModeActiveForViewer;

type GetViewerCanScheduleDraftPosts = (state: YammerState) => boolean;
export const isDraftPostsEnabled: GetViewerCanScheduleDraftPosts = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanScheduleDraftPosts;

type GetViewerCanCreateMultiLanguageMessage = (state: YammerState) => boolean;
export const getViewerCanCreateMultiLanguageMessage: GetViewerCanCreateMultiLanguageMessage = (state) =>
  !!state.featureAccessSettings.fulfilled?.viewerCanCreateMultiLanguageMessage;
